import { SetterOrUpdater } from 'recoil';
import { UISettings } from '@/state/uiSettings';

/**
 * Service to initialize UI settings once at app startup.
 * This replaces multiple API calls with a single fetch operation.
 */
export class UISettingsService {
  private static initialized = false;
  private static settings: UISettings | null = null;

  /**
   * Initialize UI settings by fetching from the API once
   */
  static async initialize(
    httpEndpoint: string,
    setSettings: SetterOrUpdater<UISettings | null>,
    setLoading: SetterOrUpdater<boolean>,
    setError: SetterOrUpdater<string | null>
  ): Promise<void> {
    if (this.initialized) {
      console.log('[UISettingsService] Already initialized, using cached settings');
      if (this.settings) {
        setSettings(this.settings);
      }
      return;
    }

    console.log('[UISettingsService] Initializing UI settings...');
    setLoading(true);
    setError(null);

    try {
      const endpoint = `${httpEndpoint}project/ui-settings`;
      const response = await fetch(endpoint, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch UI settings: ${response.status} ${response.statusText}`);
      }

      const data = await response.text();
      let parsedSettings: UISettings;

      try {
        parsedSettings = JSON.parse(data);
      } catch (parseError) {
        throw new Error(`Failed to parse UI settings response: ${parseError}`);
      }

      // Validate the settings structure
      if (!parsedSettings || typeof parsedSettings !== 'object') {
        throw new Error('Invalid UI settings structure received');
      }

      // Set default values for missing properties
      const finalSettings: UISettings = {
        voiceMode: parsedSettings.voiceMode ?? false,
        showThemeToggle: parsedSettings.showThemeToggle ?? true,
        showLogoutButton: parsedSettings.showLogoutButton ?? true,
        assistantMicEnabled: parsedSettings.assistantMicEnabled ?? false,
        colors: parsedSettings.colors,
        titles: parsedSettings.titles,
        fileUpload: parsedSettings.fileUpload ?? {
          maxSizeMB: 25,
          maxFiles: 5,
          allowedExtensions: ['pdf', 'doc', 'docx', 'txt', 'json', 'csv', 'xlsx', 'jpg', 'jpeg', 'png']
        },
        tiles: parsedSettings.tiles ?? []
      };

      this.settings = finalSettings;
      this.initialized = true;

      setSettings(finalSettings);
      setLoading(false);

      console.log('[UISettingsService] UI settings initialized successfully:', finalSettings);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('[UISettingsService] Failed to initialize UI settings:', error);
      
      setError(errorMessage);
      setLoading(false);

      // Set fallback default settings on error
      const fallbackSettings: UISettings = {
        voiceMode: false,
        showThemeToggle: true,
        showLogoutButton: true,
        fileUpload: {
          maxSizeMB: 25,
          maxFiles: 5,
          allowedExtensions: ['pdf', 'doc', 'docx', 'txt', 'json', 'csv', 'xlsx', 'jpg', 'jpeg', 'png']
        },
        tiles: []
      };

      this.settings = fallbackSettings;
      setSettings(fallbackSettings);
    }
  }

  /**
   * Get cached settings (useful for non-React contexts)
   */
  static getCachedSettings(): UISettings | null {
    return this.settings;
  }

  /**
   * Reset the service (useful for testing)
   */
  static reset(): void {
    this.initialized = false;
    this.settings = null;
  }
}